import { List, ListItemButton, ListItemText, ListProps } from '@mui/material'
import { CatalogProduct } from '../types'
import { useTranslation } from 'react-i18next'
import { CatalogProductType } from '../enums'
import { formatApperentPower, formatNominalCurrent, formatNominalVoltage } from '../utils/string'

export interface CatalogProductDetailsListProps extends Omit<ListProps, 'children'> {
  catalogProduct: CatalogProduct
  showName?: boolean
  showManufacturer?: boolean
  showManufacturerProductCode?: boolean
  showCustomData?: boolean
  includeMissing?: boolean
}

export default function CatalogProductDetailsList({
  catalogProduct,
  showName = true,
  showManufacturer = true,
  showManufacturerProductCode = true,
  showCustomData = true,
  includeMissing = true,
  ...props
}: CatalogProductDetailsListProps) {
  const { t } = useTranslation('catalogProduct')

  return (
    <List {...props}>
      {showName && (includeMissing || catalogProduct?.name) && (
        <ListItemButton key="manufacturer">
          <ListItemText primary={catalogProduct.name ?? '-'} secondary={t('name')} />
        </ListItemButton>
      )}

      {showManufacturer && (includeMissing || catalogProduct?.manufacturer) && (
        <ListItemButton key="manufacturer">
          <ListItemText
            primary={catalogProduct.manufacturer ?? '-'}
            secondary={t('manufacturer')}
          />
        </ListItemButton>
      )}

      {showManufacturerProductCode &&
        (catalogProduct?.manufacturerProductCode || includeMissing) && (
          <ListItemButton key="manufacturerProductCode">
            <ListItemText
              primary={catalogProduct.manufacturerProductCode ?? '-'}
              secondary={t('manufacturerProductCode')}
            />
          </ListItemButton>
        )}

      {catalogProduct.type === CatalogProductType.GENERATOR &&
        showCustomData &&
        (catalogProduct?.customData?.apparentPower !== undefined || includeMissing) && (
          <ListItemButton key="customData.apparentPower">
            <ListItemText
              primary={
                catalogProduct?.customData?.apparentPower !== undefined
                  ? formatApperentPower(catalogProduct.customData.apparentPower)
                  : '-'
              }
              secondary={t('generator:apparentPower')}
            />
          </ListItemButton>
        )}

      {catalogProduct.type === CatalogProductType.GENERATOR &&
        showCustomData &&
        (catalogProduct?.customData?.nominalVoltage !== undefined || includeMissing) && (
          <ListItemButton key="customData.nominalVoltage">
            <ListItemText
              primary={
                catalogProduct?.customData?.nominalVoltage !== undefined
                  ? formatNominalVoltage(catalogProduct.customData.nominalVoltage)
                  : '-'
              }
              secondary={t('generator:nominalVoltage')}
            />
          </ListItemButton>
        )}

      {catalogProduct.type === CatalogProductType.GENERATOR &&
        showCustomData &&
        (catalogProduct?.customData?.nominalCurrent !== undefined || includeMissing) && (
          <ListItemButton key="customData.nominalCurrent">
            <ListItemText
              primary={
                catalogProduct?.customData?.nominalCurrent !== undefined
                  ? formatNominalCurrent(catalogProduct.customData.nominalCurrent)
                  : '-'
              }
              secondary={t('generator:nominalCurrent')}
            />
          </ListItemButton>
        )}

      {catalogProduct.type === CatalogProductType.GENERATOR &&
        showCustomData &&
        (catalogProduct?.customData?.powerSource !== undefined || includeMissing) && (
          <ListItemButton key="customData.powerSource">
            <ListItemText
              primary={
                catalogProduct?.customData?.powerSource !== undefined
                  ? t(`generator:${catalogProduct.customData.powerSource}`)
                  : '-'
              }
              secondary={t('generator:powerSource')}
            />
          </ListItemButton>
        )}
    </List>
  )
}
